import { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Highlight, Snippet } from 'react-instantsearch-dom'
import { AlgoliaArticle, AlmanacFile } from 'types'
import { getInitials } from 'utils'
import Avatar from 'common/Avatar/Avatar'
import Styles from './ListResultItem.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useModals } from 'almanac-ui/Modal'
import { useButton } from '@react-aria/button'
import { useFocusRing } from '@react-aria/focus'
import { mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { LastEditedAt } from '../LastEditedAt'
import { FileIcon } from 'screens/Finder/components/FileIcon/FileIcon'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  item: AlgoliaArticle
}

export const ListResultItem = ({ item }: Props) => {
  const { closeAllModals } = useModals()
  const { url, creator_image_url, creator_name, last_edited_at } = item
  const ref = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const { isTabletPortrait, isMobile } = useResponsiveDesign()

  const handleClick = () => {
    history.push(url || '')
    closeAllModals()
  }
  const { buttonProps, isPressed } = useButton({ elementType: 'div', onPress: handleClick }, ref)
  const { isFocusVisible, focusProps } = useFocusRing()
  const file = {
    database_mode: item.database_mode,
    default_view: item.default_view,
    header_emoji: item.header_emoji,
    channel_emoji: item.channel_emoji,
    type: item.file_type === 'project' ? 'folder' : 'article',
    isExpanded: false,
  } as any as AlmanacFile

  return (
    <div
      className={cx(Styles.listItem, {
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocusVisible,
        [Styles.isOneLine]: item.file_type !== 'article' || item.is_blank,
      })}
      ref={ref}
      {...mergeProps(buttonProps, focusProps)}
      data-testid="list-item"
    >
      <div className={Styles.file}>
        <div className={Styles.icon}>
          <FileIcon file={file} size={isMobile ? 'lg' : 'md'} color="var(--dark-80)" />
        </div>
        <div className={Styles.searchResultInfo}>
          <div className={Styles.header} data-testid={item.title}>
            <Heading level={5}>
              <Highlight attribute="title" tagName="span" hit={item} />
            </Heading>
          </div>

          {!isTabletPortrait && (
            <div className={Styles.summary}>
              <Snippet attribute="plain_text" tagName="span" hit={item} />
            </div>
          )}
        </div>
      </div>
      {!isMobile && (
        <div className={Styles.lastEdited}>
          <Text variant="secondary">
            <LastEditedAt date={last_edited_at} />
          </Text>
        </div>
      )}

      {!isTabletPortrait && (
        <div className={Styles.owner}>
          <Text variant="secondary">
            <Avatar url={creator_image_url} label={getInitials(creator_name)} size={24} /> {creator_name}
          </Text>
        </div>
      )}
    </div>
  )
}
